import { useQuery, useQueryClient } from '@tanstack/react-query'
import { resourceCenterApi, resourceProfilesApi } from '@/api/resourceCenterApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * The Center Setup lists (z8nrz7f0vu). Every key carries `selectedTenantId`:
 * these are one center's shifts and pick lists, so a tenant switch must land
 * on a cold key rather than paint the previous center's rows.
 */
const key = (tenantId: string | null, part: string) => ['resource-center', tenantId, part] as const

export function useShiftDefinitions() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: key(tenantId, 'shifts'),
    queryFn: resourceCenterApi.listShifts,
    enabled: !!tenantId,
  })
}

export function useShiftPatterns() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: key(tenantId, 'patterns'),
    queryFn: resourceCenterApi.listPatterns,
    enabled: !!tenantId,
  })
}

export function useSupportActivityTypes() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: key(tenantId, 'support-activity-types'),
    queryFn: resourceCenterApi.listSupportActivityTypes,
    enabled: !!tenantId,
  })
}

export function useUnavailabilityTypes() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: key(tenantId, 'unavailability-types'),
    queryFn: resourceCenterApi.listUnavailabilityTypes,
    enabled: !!tenantId,
  })
}

/**
 * Refetch every Center Setup list after a write. The whole family, not just
 * the list written to: moving the Day shift's times changes the hours every
 * pattern on it reports, and loading the defaults touches all four.
 */
export function useInvalidateResourceCenter() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  // Profiles and capacity read these lists too: a shift's new times are every
  // assigned person's new hours.
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['resource-center', tenantId] }),
      qc.invalidateQueries({ queryKey: ['resource-profiles', tenantId] }),
    ])
}

// ─── Groups & resources (z8nrz7f0vw) ──────────────────────────────────────────

const profileKey = (tenantId: string | null, part: string) => ['resource-profiles', tenantId, part] as const

export function useAssignmentGroups() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: profileKey(tenantId, 'groups'),
    queryFn: resourceProfilesApi.listGroups,
    enabled: !!tenantId,
  })
}

/** Teams with their groups (z8nrz7f7dk). */
export function useTeams() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: profileKey(tenantId, 'teams'),
    queryFn: resourceProfilesApi.listTeams,
    enabled: !!tenantId,
  })
}

/** Forms a group can hold, with their procedures — only fetched while the editor is open. */
export function useGroupFormOptions(enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: profileKey(tenantId, 'form-options'),
    queryFn: resourceProfilesApi.groupFormOptions,
    enabled: enabled && !!tenantId,
    staleTime: 60_000,
  })
}

export function useResources() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: profileKey(tenantId, 'resources'),
    queryFn: resourceProfilesApi.listResources,
    enabled: !!tenantId,
  })
}

/** One person's whole profile, history included. */
export function useResourceProfile(userId: string | null) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [...profileKey(tenantId, 'profile'), userId] as const,
    queryFn: () => resourceProfilesApi.getProfile(userId as string),
    enabled: !!tenantId && !!userId,
  })
}

/** Daily capacity for the center, or one person, over a window (≤ 62 days). */
export function useResourceCapacity(from: string, to: string, userId?: string) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [...profileKey(tenantId, 'capacity'), from, to, userId ?? null] as const,
    queryFn: () => resourceProfilesApi.capacity(from, to, userId),
    enabled: !!tenantId,
    placeholderData: prev => prev,
  })
}

/** A Variable shift's days in a window. */
export function useShiftDays(userId: string, from: string, to: string, enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [...profileKey(tenantId, 'shift-days'), userId, from, to] as const,
    queryFn: () => resourceProfilesApi.listShiftDays(userId, from, to),
    enabled: enabled && !!tenantId,
  })
}

/**
 * Refetch groups and the resource rows together: a group rename shows on every
 * member's row, and moving someone between groups changes the group's members.
 */
export function useInvalidateResourceProfiles() {
  const qc = useQueryClient()
  const { selectedTenantId: tenantId } = useTenant()
  return () => qc.invalidateQueries({ queryKey: ['resource-profiles', tenantId] })
}
